import { ScaleElement } from '@scale/base-element';
import { formatMoney } from '@scale/currency';

class ProductStickyAtc extends ScaleElement {
  #form;
  #observer;

  setup() {
    this.#form = this.closest('product-form');
    const options = { signal: this.signal };
    this.#form.addEventListener('product-form:purchase', (event) => this.#update(event.detail), options);
    this.#form.addEventListener('product-form:adding', (event) => this.#showAddState(event.detail.state), options);
    this.#form.querySelector('input[name="quantity"]')?.addEventListener('input', () => this.#showQuantity(), options);
    this.onDocument('ova:layout-mutated', () => this.#follow());
    this.#follow();
    if (this.#form.purchase) this.#update(this.#form.purchase);
  }

  teardown() {
    this.#observer?.disconnect();
  }

  addToCart() {
    this.#form.querySelector('form').requestSubmit();
  }

  editOptions() {
    const picker = this.#form.querySelector('product-variant-picker');
    const target = picker.closest('scale-variant-popup')?.querySelector('[data-ref="trigger"]') ?? picker.querySelector('input:checked, select');
    target.scrollIntoView({ block: 'center' });
    target.focus({ preventScroll: true });
  }

  #follow() {
    const addButton = this.#form.querySelector('[data-ref="add-to-cart"]');
    const edit = this.ref('sticky-edit');
    if (edit) edit.hidden = !this.#form.querySelector('product-variant-picker');

    this.#observer?.disconnect();
    this.classList.remove('is-visible');
    this.inert = true;
    this.hidden = document.documentElement.dataset.ovaPreview === 'true';
    if (this.hidden) return;

    const footer = document.querySelector('.shopify-section-group-footer-group');
    let passed = false;
    let footerShown = false;
    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === addButton) passed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        else footerShown = entry.isIntersecting;
      }
      this.classList.toggle('is-visible', passed && !footerShown);
      this.inert = !passed || footerShown;
    });
    this.#observer.observe(addButton);
    if (footer) this.#observer.observe(footer);
  }

  #update({ variant, total, compare, conflict, label }) {
    const title = this.ref('sticky-variant');
    if (title) title.textContent = variant.title;
    this.ref('sticky-current-price').textContent = formatMoney(total);

    const was = this.ref('sticky-compare');
    was.hidden = compare <= total;
    was.textContent = was.hidden ? '' : formatMoney(compare);

    const submit = this.ref('sticky-submit');
    this.ref('sticky-label').textContent = label;
    submit.disabled = !variant.available || conflict || submit.dataset.variantBundleDisabled === 'true';
    submit.dataset.variantAvailable = String(variant.available);
    submit.dataset.bundleConflict = String(conflict);
    this.#showQuantity();
  }

  #showQuantity() {
    const quantity = Number(this.#form.querySelector('input[name="quantity"]')?.value) || 1;
    const shown = this.ref('sticky-quantity');
    shown.textContent = quantity;
    shown.hidden = quantity <= 1;
  }

  #showAddState(state) {
    const submit = this.ref('sticky-submit');
    if (state) submit.dataset.state = state;
    else delete submit.dataset.state;
    if (state === 'loading') submit.setAttribute('aria-busy', 'true');
    else submit.removeAttribute('aria-busy');
  }
}

if (!customElements.get('product-sticky-atc')) {
  customElements.define('product-sticky-atc', ProductStickyAtc);
}
